import {
  ORG_WORKSPACE_TOUR_STEPS,
  type OrgWorkspaceTourStepId,
} from './orgWorkspaceTourSteps';

/**
 * The tour runs outside React, so the AORs tab registers the drill-down it
 * alone can perform, and the hub map layout registers moving in and out of the
 * AOR View SitStat. Everything is optional: a step whose host is not mounted
 * simply has nothing to reveal.
 */
export type OrgWorkspaceTourApi = {
  showOverview: () => void;
  expandFirstDistrict: () => void;
  revealSectorSubunits: () => void;
  openSectorSubunits: () => void;
  showAreaDetails: () => void;
  showDistrictDetails: () => void;
  showSectorDetails: () => void;
  showSubunitDetails: () => void;
  /** Opens a subunit card with View Assets expanded for FOC-ORG-001e. */
  showOwnershipAssets: () => void;
  showAreaOnMap: () => void;
  zoomToFirstDistrict: () => void;
  showAllAreasOnMap: () => void;
  firstAreaId: () => string | null;
  /** Registered by the hub map layout. */
  isSitStatOpen: () => boolean;
  openAorViewSitStat: (aorId: string | null) => void;
  exitSitStatToAors: () => void;
};

let api: Partial<OrgWorkspaceTourApi> = {};
/** An AORs-tab step waiting for the tab to remount after leaving SitStat. */
let pendingStep: OrgWorkspaceTourStepId | null = null;
let lastAreaId: string | null = null;

export function registerOrgWorkspaceTourApi(
  partial: Partial<OrgWorkspaceTourApi>
): () => void {
  api = { ...api, ...partial };
  if (pendingStep && api.showOverview) {
    const step = pendingStep;
    pendingStep = null;
    prepareOrgWorkspaceStep(step);
  }
  return () => {
    for (const key of Object.keys(partial) as (keyof OrgWorkspaceTourApi)[]) {
      if (api[key] === partial[key]) {
        delete api[key];
      }
    }
  };
}

export function getOrgWorkspaceTourApi(): Partial<OrgWorkspaceTourApi> {
  return api;
}

export function isOrgWorkspaceTourOnSitStat(): boolean {
  return getOrgWorkspaceTourApi().isSitStatOpen?.() === true;
}

/** Puts the screen in the state the step highlights. */
export function prepareOrgWorkspaceStep(id: OrgWorkspaceTourStepId) {
  const host = getOrgWorkspaceTourApi();
  const surface =
    ORG_WORKSPACE_TOUR_STEPS.find((step) => step.id === id)?.surface ?? 'aors';

  if (surface === 'sitstat') {
    pendingStep = null;
    if (host.isSitStatOpen?.()) return;
    lastAreaId = host.firstAreaId?.() ?? lastAreaId;
    host.openAorViewSitStat?.(lastAreaId);
    return;
  }

  if (host.isSitStatOpen?.()) {
    pendingStep = id;
    host.exitSitStatToAors?.();
    return;
  }

  switch (id) {
    case 'intro':
    case 'structure':
    case 'filterArea':
    case 'orgEntry':
    case 'orgData':
    case 'orgHomeScope':
      host.showOverview?.();
      return;
    case 'expandDistrict':
      host.expandFirstDistrict?.();
      return;
    case 'expandSector':
      host.revealSectorSubunits?.();
      return;
    case 'navigate':
    case 'orgDrillDown':
      host.openSectorSubunits?.();
      return;
    case 'orgOwnership':
      host.showOwnershipAssets?.();
      return;
    case 'areaDetails':
      host.showAreaDetails?.();
      return;
    case 'districtDetails':
      host.showDistrictDetails?.();
      return;
    case 'sectorDetails':
      host.showSectorDetails?.();
      return;
    case 'subunitDetails':
    case 'orgGeospatial':
      host.showSubunitDetails?.();
      return;
    case 'areaOnMap':
      host.showAreaOnMap?.();
      return;
    case 'zoomOnMap':
      host.zoomToFirstDistrict?.();
      return;
    case 'allAreasOnMap':
    case 'orgContext':
    case 'orgSymbology':
      host.showAllAreasOnMap?.();
      return;
  }
}

/** Test-only reset of module state. */
export function resetOrgWorkspaceTourBridgeForTests() {
  api = {};
  pendingStep = null;
  lastAreaId = null;
}
